import { FishbonePoint } from './FishbonePoint';
import { sortFishboneItems } from 'utils/fishboneSort';

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import React, { useRef, useState, useEffect } from 'react';

import './FishboneBranch.sass';

dayjs.extend(relativeTime)

// Délai de base pour l'effet wave (en ms)
const WAVE_BASE_DELAY = 600;
const WAVE_STEP_DELAY = 80;

export const FishboneBranchContent = (props: any) => {
	// Track des IDs pour éviter le double effet d'animation
	// quand l'ID passe de 'temp-id' à l'ID réel
	const prevDataIdsRef = useRef<Set<string> | null>(null);
	const [appearingIds, setAppearingIds] = useState<Set<string>>(new Set());

	// Tri stable des sous-arêtes (createdAt puis id) : sans cela elles etaient
	// affichees dans l'ordre brut du reseau, qui change a chaque refetch.
	const allPoints = sortFishboneItems(props.points || []);

	useEffect(() => {
		const currentIds = new Set(allPoints.map((p: any) => p.id));
		const prevIds = prevDataIdsRef.current;

		// Premier rendu - initialiser sans animation
		if (prevIds === null) {
			prevDataIdsRef.current = currentIds;
			return;
		}

		// Détecter les nouveaux IDs (pas présents avant)
		const newAppearingIds = new Set<string>();
		currentIds.forEach(id => {
			if (!prevIds.has(id)) {
				// Si l'ancien set contenait 'temp-id' et qu'on a un nouvel ID,
				// c'est juste le remplacement optimistic -> réel, pas une nouvelle apparition
				if (!(prevIds.has('temp-id') && id !== 'temp-id')) {
					newAppearingIds.add(id);
				}
			}
		});

		prevDataIdsRef.current = currentIds;

		if (newAppearingIds.size > 0) {
			setAppearingIds(newAppearingIds);
			const timer = setTimeout(() => {
				setAppearingIds(new Set());
			}, 1500);
			return () => clearTimeout(timer);
		}
	}, [allPoints]);

	const isAppearing = (id: string): boolean => {
		return appearingIds.has(id);
	};

	const startY = props.position == 'bottom' ? props.height : 0;

	const y = startY;

	const branchLength = Math.sqrt(
		(props.height - y) * (props.height - y) + props.width * props.width
	);

	//sortBy(props.points || [], (p) => (p && p.completion) || 0).reverse()
	const segmentSize = branchLength / allPoints.length;

	let pointsWeight = 0;
	allPoints.forEach((p) => (pointsWeight += p.weight || 1));

	// Calcul de l'index global de la branche pour l'effet wave échelonné
	const branchIndex = props.branchIndex || 0;

	const points = allPoints.map((p, index) => {

		const segmentWidth = props.width / allPoints.length;
		const segmentHeight = props.height / allPoints.length;
		const deltaIndex = props.odd ? 0.25 : 0.5;

		const startY = props.position == 'bottom' ? props.height / 2 : 0;

		const posX = segmentWidth * (index + deltaIndex);
		const posY = (props.position == 'bottom' ? -1 : 1) * (segmentHeight * (index + deltaIndex)) + startY

		const isBeingDragged = props.draggingItem && props.draggingItem.id === p.id;
		
		// Calcul du délai d'animation wave basé sur l'index de la branche et l'index du point
		const waveDelay = WAVE_BASE_DELAY + (branchIndex * 100) + (index * WAVE_STEP_DELAY);
		
		return !isBeingDragged ? (
			<FishbonePoint
				key={p.id}
				{...p}
				x={posX}
				y={posY + startY}
				isMatching={props.matchingIndexedItems[p.id]}
				isAppearing={isAppearing(p.id)}
				animationDelay={waveDelay}
				animationIndex={index}
				onSelectItem={props.onSelectItem}
				selected={props.selection && props.selection.id == p.id || false}

				size={0.15 * segmentSize}
				onUpdate={props.onUpdateItem}
				onEdit={props.onEditItem}
				onStartDragItem={props.onStartDragItem}
				onItemDragEnter={props.onItemDragEnter}
				isDropTarget={props.dropTargetItemId === p.id}
				dropTargetSide={props.dropTargetSide}
				slotWidth={segmentWidth}
				slotHeight={segmentHeight}
				draggingItem={props.draggingItem}
			></FishbonePoint>
		) : null;
	});

	return (
		<g
			className="FishboneBranchContent"
		>
			<g className="points">{points}</g>
		</g>
	);
};
